import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import DeleteIcon from '@mui/icons-material/Delete'

import Spinner from 'components/Spinner'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import { color } from 'styles/colors'

import { monthFullName } from 'helpers/formatters'

import { Conversation, GroupedConversations } from 'types/Chat'

type Props = {
  groupedConversations: GroupedConversations
  loading: boolean
  loadingMore: boolean
  nextToken: string | null
  error: string | null
  selectedConversationId: string | null
  listConversationsCallback: (token?: string | null, append?: boolean) => Promise<void>
  onSelectConversation: (id: string) => void
  onDeleteConversation: (id: string) => void
}

const ConversationList = ({ groupedConversations, loading, loadingMore, nextToken, error, selectedConversationId, listConversationsCallback, onSelectConversation, onDeleteConversation }: Props) => {
  const navigate = useNavigate()

  const hasMore = nextToken !== null
  const currentMonth = useMemo(() => monthFullName(Date.now()), [])

  const handleSelectConversation = useCallback((conversation: Conversation) => {
    onSelectConversation(conversation.conversationId)
    navigate(`?conversationId=${conversation.conversationId}`)
  }, [onSelectConversation, navigate])

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop <= clientHeight + 200 && hasMore && !loadingMore && !loading) {
      listConversationsCallback(nextToken, true)
    }
  }, [hasMore, loadingMore, loading, listConversationsCallback, nextToken])

  if (loading) {
    return <S.Container><Spinner variant="page" /></S.Container>
  }

  if (error) {
    return <S.Container><S.ErrorText>{error}</S.ErrorText></S.Container>
  }

  return (
    <S.Container onScroll={handleScroll}>
      {Object.entries(groupedConversations).map(([month, monthConversations]) => (
        <div key={month}>
          <S.MonthHeader>{month === currentMonth ? 'Previous Conversations' : month}</S.MonthHeader>
          {monthConversations.map((conversation) => (
            <S.List key={conversation.conversationId}>
              <S.Conversation
                $isSelected={selectedConversationId === conversation.conversationId}
                onClick={() => handleSelectConversation(conversation)}
              >
                {conversation.firstMessage}
              </S.Conversation>
              <S.DeleteButton onClick={() => onDeleteConversation(conversation.conversationId)}>
                <DeleteIcon fontSize="small" />
              </S.DeleteButton>
            </S.List>
          ))}
        </div>
      ))}
      <S.ButtonContainer>
        {loadingMore && <Spinner variant="page" />}
        {!loadingMore && hasMore && (
          <Button decoration="positive" text="Load more" onPress={() => listConversationsCallback(nextToken, true)} />
        )}
      </S.ButtonContainer>
    </S.Container>
  )
}

export default ConversationList

const S = {
  Container: styled.div`
    overflow-y: auto;
    max-height: calc(100vh - 300px);
  `,
  List: styled.div`
    display: flex;
    gap: 10px;
    padding: 8px 0;
    min-width: 0;
    justify-content: space-between;
    width: 100%;
  `,
  Conversation: styled.button<{ $isSelected: boolean }>`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    text-align: left;
    overflow: hidden;
    white-space: nowrap;
    font-weight: ${({ $isSelected }) => ($isSelected ? '700' : '500')};
    text-overflow: ellipsis;
    color: ${color('chatbotQuestion')};
  `,
  MonthHeader: styled.div`
    font-size: 14px;
    font-weight: 400;
    margin-top: 20px;
    color: ${color('chatbotConversationMonth')};
  `,
  ButtonContainer: styled.div`
    display: flex;
    justify-content: center;
    margin-top: 20px;
    
    button{
      border-radius: 15px !important;
      div{
        text-transform: capitalize !important;
      }
    }
  `,
  ErrorText: styled.p`
    font-size: 14px;
    text-align: center;
    padding: 16px;
    color: ${color('errorMessage')};
  `,
  DeleteButton: styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    color: ${color('chatbotQuestion')};
    opacity: 0.6;
    flex-shrink: 0;

    &:hover {
      opacity: 1;
    }
  `
}
